/**
 * collaboratorsService.js — wrappers over the Django `collaborators` API.
 *
 * All requests go through the shared `httpClient` (session cookie + CSRF).
 * Owner-only endpoints (invitations, members) 403 for non-owners; accept is
 * public. See `collaborators/views.py`.
 *
 * Endpoints (mounted at /api/v1/collaborators/):
 *   GET/POST /invitations/                 list pending / create (Pro-only)
 *   DELETE   /invitations/<id>/            revoke
 *   POST     /invitations/<id>/resend/     resend
 *   GET      /members/                     list active members
 *   PATCH    /members/<user_id>/           update capability toggles
 *   DELETE   /members/<user_id>/           remove
 *   GET/POST /invitations/accept/          public: info / accept
 */
import httpClient from './httpClient';

const BASE = '/collaborators';

/** GET /invitations/ → { results: Invitation[], seats: {used,limit,remaining} } */
export async function getInvitations() {
  const { data } = await httpClient.get(`${BASE}/invitations/`);
  return {
    results: Array.isArray(data?.results) ? data.results : [],
    seats: data?.seats || { used: 0, limit: 0, remaining: 0 },
  };
}

/**
 * POST /invitations/ — body { email, ...permissions, location_ids? }.
 * `permissions` is a flat snake_case map of every capability toggle (view_* and
 * edit caps). There is no role — the employee is defined by this map. The
 * backend ignores unknown/owner-only keys.
 *
 * `locationIds` es el OTRO eje: a qué locales llega. Va como clave propia y no
 * aplanado con las capacidades, porque el backend solo recoge del cuerpo las
 * claves de su lista de capacidades y descarta el resto **en silencio**. Vacío
 * o ausente = todos los locales.
 */
export async function createInvitation({ email, permissions = {}, locationIds }) {
  const { data } = await httpClient.post(`${BASE}/invitations/`, {
    email,
    ...permissions,
    ...(Array.isArray(locationIds) ? { location_ids: locationIds } : {}),
  });
  return data;
}

/** DELETE /invitations/<id>/ */
export async function revokeInvitation(invitationId) {
  await httpClient.delete(`${BASE}/invitations/${encodeURIComponent(invitationId)}/`);
}

/** POST /invitations/<id>/resend/ */
export async function resendInvitation(invitationId) {
  const { data } = await httpClient.post(
    `${BASE}/invitations/${encodeURIComponent(invitationId)}/resend/`, {},
  );
  return data;
}

/** GET /members/ → { results: Member[], seats } */
export async function getMembers() {
  const { data } = await httpClient.get(`${BASE}/members/`);
  return {
    results: Array.isArray(data?.results) ? data.results : [],
    seats: data?.seats || { used: 0, limit: 0, remaining: 0 },
  };
}

/**
 * PATCH /members/<user_id>/ — { permissions?, active?, locationIds? } (parcial).
 *
 * `location_ids` solo viaja si se pasa un array, y eso es load-bearing: la card
 * manda un PATCH por cada interruptor de permisos que se toca, y el backend
 * distingue «la clave no viene» (no tocar) de «viene vacía» (quitar el
 * acotado). Mandarla siempre borraría el acotado en cada toggle.
 */
export async function updateMember(userId, { permissions, active, locationIds } = {}) {
  const body = {};
  if (permissions) Object.assign(body, permissions);
  if (active !== undefined) body.active = active;
  if (Array.isArray(locationIds)) body.location_ids = locationIds;
  const { data } = await httpClient.patch(
    `${BASE}/members/${encodeURIComponent(userId)}/`, body,
  );
  return data;
}

/** DELETE /members/<user_id>/ */
export async function removeMember(userId) {
  await httpClient.delete(`${BASE}/members/${encodeURIComponent(userId)}/`);
}

/**
 * GET /activity/ — owner-facing audit log (tenant activity feed).
 * → { results: Event[], page, pageSize, total, categories }
 * Each Event: { id, action, category, actor:{type,name,id,email}, target, metadata, at }.
 * A `support` actor is already masked to "Soporte Tapstar" (no id/email) by the backend.
 * `from`/`to` are inclusive `YYYY-MM-DD` calendar days (UTC).
 * @param {{ page?: number, pageSize?: number, category?: string, actorId?: string, from?: string, to?: string }} [opts]
 */
export async function getActivityLog({
  page = 1, pageSize = 25, category, actorId, from, to,
} = {}) {
  const params = { page, page_size: pageSize };
  if (category) params.category = category;
  if (actorId) params.actor_id = actorId;
  if (from) params.from = from;
  if (to) params.to = to;
  const { data } = await httpClient.get(`${BASE}/activity/`, { params });
  return {
    results: Array.isArray(data?.results) ? data.results : [],
    page: data?.page || 1,
    pageSize: data?.pageSize || pageSize,
    total: data?.total || 0,
    categories: Array.isArray(data?.categories) ? data.categories : [],
  };
}

/** GET /invitations/accept/?token= (public) → { email, permissions, tenantOwnerName } */
export async function getInvitationInfo(token) {
  const { data } = await httpClient.get(`${BASE}/invitations/accept/`, {
    params: { token },
  });
  return data;
}

/** POST /invitations/accept/ (public) — body { token, name, password, phone, phone_prefix } */
export async function acceptInvitation({ token, name, password, phone, phonePrefix }) {
  const { data } = await httpClient.post(`${BASE}/invitations/accept/`, {
    token, name, password, phone, phone_prefix: phonePrefix,
  });
  return data;
}
